import { IssueStatus, portalIssueStatusLabel } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';

/**
 * Pill colors matching the storefront's transaction-status treatment, from
 * the customer's side of the conversation (PD-16525): "Awaiting your reply"
 * is the one status that asks something of them, so it is the one that
 * stands out; a Closed request is archived and reads as neutral.
 */
function statusClasses(status: string): string {
	switch (status as IssueStatus) {
		case IssueStatus.NEEDS_REPLY:
		case IssueStatus.TODO:
			return 'bg-accent-badge text-accent-tint-fg border border-accent-tint';
		case IssueStatus.IN_PROGRESS:
			return 'bg-warning-badge text-warning-tint-fg border border-warning-tint';
		case IssueStatus.WAITING_ON_CUSTOMER:
			return 'bg-danger-badge text-danger-tint-fg border border-danger-tint';
		case IssueStatus.RESOLVED:
			return 'bg-success-badge text-success-tint-fg border border-success-tint';
		case IssueStatus.DONE:
		default:
			return 'bg-sunken text-strong';
	}
}

/**
 * Status pill for a support request (PD-13773) — shared by the Support
 * list and the issue-thread page so the two always agree. The wording is the
 * customer-facing label (`portalIssueStatusLabel`): the CRM's "Needs Reply"
 * reads "Open", "Waiting on Customer" reads "Awaiting your reply" and "Done"
 * reads "Closed".
 */
export function IssueStatusChip({ status }: { status: string }): JSX.Element {
	const { t } = useTranslation();

	return (
		<span
			className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${statusClasses(status)}`}
		>
			{t(portalIssueStatusLabel(status))}
		</span>
	);
}
